import { COLORS, Col, FlexView, Row } from '@cuemath/comps';
import { Button, Image, Separator, Text } from '@cuemath/web-comps';
import React, { Fragment } from 'react';
import styled from 'styled-components';
import TeacherDetails from '../teacher-details';
import useLogic from './logic';
import { Loader } from 'semantic-ui-react';
import SearchIcon from 'img/search.svg';
import StarPurple from 'img/star-purple.svg';
import { getDefaultDaysOptions, getTableHeaderDateOptions } from '../../helpers';
import PropTypes from 'prop-types';

const Wrapper = styled(FlexView)`
  max-height: 460px;
  overflow-y: auto;
  border-radius: 8px;
  border: 1px solid ${COLORS.GRAY_XXLIGHT};
  ::-webkit-scrollbar {
    width: 4px;
  }
`;
const Column = styled(FlexView)`
  border-right: 1px solid ${COLORS.GRAY_XXLIGHT};
  flex: 1;
  max-width: ${({ viewNextPage }) => (viewNextPage ? '25%' : '20%')};
  border-bottom: 1px solid ${COLORS.GRAY_XXLIGHT};
  &:last-child {
    border-right: none;
  }
`;
const TableHeader = styled(FlexView)`
  position: sticky; 
  top: 0
  box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.10);
  background-color: ${COLORS.WHITE};
`;
const TimeSlot = styled(FlexView)`
  background-color: ${COLORS.GRAY_XXXLIGHT};
  border-radius: 4px;
  border: 1px solid ${COLORS.GHOST};
`;
const SlotsWrapper = styled(Row)`
  flex-wrap: wrap;
`;

const SlotsColumn = styled(Column)`
  cursor: pointer;
  &:hover {
    background-color: ${COLORS.GRAY_XXXLIGHT};
    > button {
      > div {
        visibility: inherit;
      }
      visibility: inherit;
    }
  }
`;
const HiddenButton = styled(Button)`
  visibility: hidden;
  > div {
    visibility: hidden;
  }
`;
const EmptyWrapper = styled(FlexView)`
  width: 100%;
`;
const Icon = styled(Image)`
  margin-right: 8px;
`;
const BannerWrapper = styled(FlexView)`
  background-color: ${COLORS.FLOWER_XXLIGHT};
`;
const ZoneText = styled(Text)`
  position: relative;
  top: -2px;
  margin-left: 4px;
`;

const TeachersListingTable = ({
  teachers,
  prospectID,
  timezone,
  viewNextPage,
  isSearchProcessing,
  referralBannerMessage,
  zone,
}) => {
  const { handleOpenBookTrialClassModal } = useLogic({ timezone, prospectID });

  const empty = isSearchProcessing ? (
    <FlexView alignItems="center">
      <Separator height={55} />
      <Loader inline active />
      <Separator height={55} />
    </FlexView>
  ) : (
    <EmptyWrapper alignItems="center">
      <Separator height={55} />
      <Image url={SearchIcon} width={24} height={24} />
      <Separator height={8} />
      <Text variant="body2" color={COLORS.COLA_LIGHTER}>
        Search prospect id to find related teachers
      </Text>
      <Separator height={55} />
    </EmptyWrapper>
  );

  const renderReferralBanner = (
    <BannerWrapper flexDirection="row" alignItems="center" gap={6} gutter={12}>
      <Icon height={24} width={24} url={StarPurple} />
      <Text variant="body3" color={COLORS.FLOWER_DARK}>
        {referralBannerMessage}
      </Text>
    </BannerWrapper>
  );

  const defaultHeaderDates = getDefaultDaysOptions(7);
  const updatedHeaderDates =
    teachers?.[0]?.availability?.length > 0
      ? getTableHeaderDateOptions(teachers[0].availability)
      : defaultHeaderDates;

  const tableHeaderDays = viewNextPage
    ? updatedHeaderDates?.slice(4)
    : updatedHeaderDates?.slice(0, 4);

  const tableHeader = (
    <TableHeader flexDirection="row">
      <Column gutter={16} gap={16} viewNextPage={viewNextPage}>
        <Text variant="eyebrow1">TUTORS</Text>
      </Column>
      {tableHeaderDays?.map(({ id, dayOfWeek, formattedDate }) => (
        <Column key={id} gutter={16} gap={16} align="center" viewNextPage={viewNextPage}>
          <FlexView flexDirection="row" alignItems="center">
            <Text variant="eyebrow1" align="center">
              {`${dayOfWeek}, ${formattedDate}`}
            </Text>
            {zone && (
              <ZoneText
                variant="body1Regular"
                color={COLORS.TEXT_LIGHT}
              >{`(${zone})`}</ZoneText>
            )}
          </FlexView>
        </Column>
      ))}
    </TableHeader>
  );

  const shouldRenderList = teachers?.length >= 1;

  const renderTeacherList = teachers?.map(
    ({
      id: teacherID,
      name,
      timeInCuemath,
      studentsTaught,
      studentsInSelectedGradeCohort,
      studentsInSelectedCountry,
      availability,
      isDAenabledTeacher,
    }) => {
      const updatedAvailability = viewNextPage
        ? availability?.slice(4)
        : availability?.slice(0, 4);

      return (
        <Fragment key={teacherID}>
          {referralBannerMessage && renderReferralBanner}
          <FlexView flexDirection="row" key={teacherID}>
            <Column viewNextPage={viewNextPage} gutter={16} gap={16}>
              <TeacherDetails
                id={teacherID}
                name={name}
                timeInCuemath={timeInCuemath}
                studentsTaught={studentsTaught}
                studentsInSelectedGradeCohort={studentsInSelectedGradeCohort}
                studentsInSelectedCountry={studentsInSelectedCountry}
                isDAenabledTeacher={isDAenabledTeacher}
              />
            </Column>
            {updatedAvailability?.map(({ id: availabilityID, date, slots }, index) => {
              const shouldRenderTheDateCol = tableHeaderDays?.[index]?.date === date;

              if (!shouldRenderTheDateCol) return null;

              const isSlotsEmpty = slots?.length === 0;

              return (
                <SlotsColumn
                  viewNextPage={viewNextPage}
                  key={availabilityID}
                  gutter={16}
                  gap={16}
                >
                  <SlotsWrapper>
                    {isSlotsEmpty ? (
                      <Text variant="body1Regular" color={COLORS.COLA_LIGHTER}>
                        No slots available
                      </Text>
                    ) : (
                      slots?.map(({ id: slotID, slotStartTime, slotEndTime }) => (
                        <Col key={slotID} gutter={4} gap={4}>
                          <TimeSlot gap={4} gutter={8}>
                            {`${slotStartTime} - ${slotEndTime}`}
                          </TimeSlot>
                        </Col>
                      ))
                    )}
                  </SlotsWrapper>
                  <Separator height={24} />
                  {!isSlotsEmpty && (
                    <HiddenButton
                      onClick={() =>
                        handleOpenBookTrialClassModal({
                          date,
                          name,
                          id: teacherID,
                        })
                      }
                      label="Select"
                      size="xsmall"
                    />
                  )}
                </SlotsColumn>
              );
            })}
          </FlexView>
        </Fragment>
      );
    },
  );

  return (
    <Wrapper>
      {tableHeader}
      <FlexView>{shouldRenderList ? renderTeacherList : empty}</FlexView>
    </Wrapper>
  );
};

TeachersListingTable.propTypes = {
  teachers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      timeInCuemath: PropTypes.string,
      studentsTaught: PropTypes.string,
      studentsInSelectedGradeCohort: PropTypes.string,
      studentsInSelectedCountry: PropTypes.string,
      isDAenabledTeacher: PropTypes.bool,
      availability: PropTypes.shape({
        id: PropTypes.string,
        date: PropTypes.string,
        slots: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string,
            slotStartTime: PropTypes.string,
            slotEndTime: PropTypes.string,
          }),
        ),
      }),
    }),
  ),
  prospectID: PropTypes.string,
  timezone: PropTypes.string,
  viewNextPage: PropTypes.bool,
  isSearchProcessing: PropTypes.bool,
  referralBannerMessage: PropTypes.string,
  zone: PropTypes.string,
};

export default TeachersListingTable;
